/**
 * Migration Studio — Layer B UI Panes
 *
 * Six functional panes (B.1–B.6) exposing the Layer B flow:
 * Costing/Quote · Control Tower · Test Migration · Reconciliation · UAT/Release · Rollback/Hypercare
 *
 * Every pane is self-contained + accepts (activeProject, setActiveProject) so
 * they can be dropped into any project-context page.
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  DollarSign, Gauge, Play, Scale, ClipboardCheck, RotateCcw,
  Loader2, RefreshCw, Sparkles, CheckCircle2, XCircle, AlertTriangle,
  ShieldAlert, TrendingUp,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

function useProjects(activeProject, setActiveProject) {
  const [projects, setProjects] = useState([]);
  useEffect(() => {
    axios.get(`${API}/api/migration/projects`).then((r) => {
      setProjects(r.data.projects || []);
      if (!activeProject && r.data.projects?.length) setActiveProject(r.data.projects[0]._id);
    }).catch(() => {});
  }, [activeProject, setActiveProject]);
  return projects;
}

function ProjectPicker({ projects, activeProject, setActiveProject, testId }) {
  return (
    <select
      value={activeProject || ''}
      onChange={(e) => setActiveProject(e.target.value)}
      className="border border-slate-200 rounded-md h-9 px-3 text-sm bg-white"
      data-testid={testId}
    >
      <option value="">Select a project…</option>
      {projects.map((p) => (
        <option key={p._id} value={p._id}>{p.project_number} · {p.project_name}</option>
      ))}
    </select>
  );
}

// ── B.1 — Costing & Quote ─────────────────────────────────────────────────

export function CostingPane({ activeProject, setActiveProject }) {
  const projects = useProjects(activeProject, setActiveProject);
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [overrideCredits, setOverrideCredits] = useState('');
  const [overrideReason, setOverrideReason] = useState('');
  const [payMode, setPayMode] = useState('prepaid');
  const [offlineReason, setOfflineReason] = useState('');

  const load = useCallback(async () => {
    if (!activeProject) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/migration/projects/${activeProject}/quote`);
      setQuote(data.quote);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  }, [activeProject]);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    if (!activeProject) return;
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API}/api/migration/projects/${activeProject}/quote/generate`);
      setQuote(data.quote);
      toast.success(`Quote ${data.quote.quote_number} generated`);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
    finally { setGenerating(false); }
  };

  const applyOverride = async () => {
    if (!activeProject || !quote) return;
    try {
      const { data } = await axios.post(`${API}/api/migration/projects/${activeProject}/quote/override`, {
        override_total_credits: parseInt(overrideCredits, 10),
        override_reason: overrideReason,
      });
      setQuote(data.quote); setOverrideCredits(''); setOverrideReason('');
      toast.success('Override applied');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const decide = async (decision) => {
    if (!activeProject || !quote) return;
    try {
      const body = { decision, payment_mode: payMode };
      if (payMode === 'pay_offline_deferred') body.pay_offline_reason = offlineReason;
      const { data } = await axios.post(`${API}/api/migration/projects/${activeProject}/quote/decision`, body);
      setQuote(data.quote);
      toast.success(`Quote ${decision}ed`);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-4" data-testid="costing-pane">
      <div className="flex items-center justify-between gap-4">
        <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-emerald-600" />Costing &amp; Quote
        </h2>
        <div className="flex gap-2 items-center">
          <ProjectPicker projects={projects} activeProject={activeProject} setActiveProject={setActiveProject} testId="costing-project-select" />
          <Button size="sm" variant="outline" onClick={load} data-testid="costing-refresh-btn"><RefreshCw className="w-4 h-4 mr-2" />Refresh</Button>
          <Button size="sm" onClick={generate} disabled={generating || !activeProject}
                  className="bg-emerald-600 hover:bg-emerald-700" data-testid="costing-generate-btn">
            {generating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating…</>
                        : <><Sparkles className="w-4 h-4 mr-2" />Generate Quote</>}
          </Button>
        </div>
      </div>

      {loading && <div className="text-center p-6"><Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" /></div>}

      {!loading && !quote && (
        <Card className="border-slate-200"><CardContent className="p-8 text-center text-slate-500">
          <DollarSign className="w-10 h-10 mx-auto text-slate-300 mb-2" />
          No quote yet. Click <span className="font-semibold">Generate Quote</span> once analysis is complete.
        </CardContent></Card>
      )}

      {!loading && quote && (
        <>
          <Card className="border-slate-200" data-testid="quote-card">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">{quote.quote_number} · v{quote.version_number}</CardTitle>
                  <div className="text-xs text-slate-500 mt-1">Complexity {quote.complexity_score}/100 · {quote.total_rows.toLocaleString()} rows · {quote.approved_mapping_count} approved mappings</div>
                </div>
                <Badge className={`text-[11px] border ${
                  quote.status === 'accepted' ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
                    : quote.status === 'rejected' ? 'bg-rose-100 text-rose-800 border-rose-200'
                    : 'bg-amber-100 text-amber-800 border-amber-200'
                }`}>{quote.status}</Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="p-3 rounded-md bg-slate-50 border border-slate-200">
                  <div className="text-[10px] uppercase tracking-wide text-slate-500">Formula Credits</div>
                  <div className="text-lg font-bold text-slate-900" data-testid="formula-credits">{quote.formula_total_credits.toLocaleString()}</div>
                  <div className="text-xs text-slate-500">×{quote.complexity_multiplier} complexity multiplier</div>
                </div>
                <div className="p-3 rounded-md bg-emerald-50 border border-emerald-200">
                  <div className="text-[10px] uppercase tracking-wide text-emerald-700">Quoted Credits</div>
                  <div className="text-lg font-bold text-emerald-800" data-testid="quoted-credits">{quote.quoted_credits.toLocaleString()}</div>
                  <div className="text-xs text-emerald-700">{quote.quoted_currency_amount} {quote.currency_code}</div>
                </div>
                <div className="p-3 rounded-md bg-blue-50 border border-blue-200">
                  <div className="text-[10px] uppercase tracking-wide text-blue-700">Payment</div>
                  <div className="text-lg font-bold text-blue-800">{quote.payment_mode || 'prepaid'}</div>
                  <div className="text-xs text-blue-700">Expires {new Date(quote.expires_at).toLocaleDateString()}</div>
                </div>
              </div>

              {quote.override_reason && (
                <div className="p-2 rounded-md bg-purple-50 border border-purple-200 text-xs" data-testid="override-notice">
                  <span className="font-semibold text-purple-800">Overridden</span>
                  <span className="text-purple-700"> — {quote.override_reason}</span>
                </div>
              )}

              {quote.status === 'issued' && (
                <div className="pt-3 border-t border-slate-200 space-y-3">
                  <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Operator Override (optional)</div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">Override credits</Label>
                      <Input type="number" value={overrideCredits} onChange={(e) => setOverrideCredits(e.target.value)}
                              placeholder="e.g. 3500" data-testid="override-credits-input" />
                    </div>
                    <div>
                      <Label className="text-xs">Reason</Label>
                      <Input value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)}
                              placeholder="e.g. Early-adopter discount" data-testid="override-reason-input" />
                    </div>
                  </div>
                  <Button size="sm" variant="outline" onClick={applyOverride}
                          disabled={!overrideCredits || !overrideReason}
                          data-testid="apply-override-btn">Apply Override</Button>

                  <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide pt-3 border-t border-slate-200">Client Decision</div>
                  <div className="flex items-center gap-2">
                    <select value={payMode} onChange={(e) => setPayMode(e.target.value)}
                             className="border border-slate-200 rounded-md h-9 px-3 text-sm bg-white"
                             data-testid="payment-mode-select">
                      <option value="prepaid">Prepaid</option>
                      <option value="pay_offline_deferred">Pay offline / deferred</option>
                      <option value="invoice_net_30">Invoice Net-30</option>
                    </select>
                    {payMode === 'pay_offline_deferred' && (
                      <Input value={offlineReason} onChange={(e) => setOfflineReason(e.target.value)}
                              placeholder="Offline reason (country operator, first-10 client, etc.)"
                              data-testid="offline-reason-input" />
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => decide('accept')}
                            className="bg-emerald-600 hover:bg-emerald-700" data-testid="quote-accept-btn">
                      <CheckCircle2 className="w-4 h-4 mr-2" />Accept
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => decide('reject')}
                            className="text-rose-700 border-rose-300 hover:bg-rose-50" data-testid="quote-reject-btn">
                      <XCircle className="w-4 h-4 mr-2" />Reject
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

// ── B.2 — Migration Control Tower ─────────────────────────────────────────

export function ControlTowerPane() {
  const [health, setHealth] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [h, p] = await Promise.all([
        axios.get(`${API}/api/migration/control-tower/health`),
        axios.get(`${API}/api/migration/control-tower/projects`),
      ]);
      setHealth(h.data); setProjects(p.data.projects || []);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="control-tower-pane">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
          <Gauge className="w-5 h-5 text-blue-600" />Migration Control Tower
        </h2>
        <Button size="sm" variant="outline" onClick={load} data-testid="control-tower-refresh-btn">
          <RefreshCw className="w-4 h-4 mr-2" />Refresh
        </Button>
      </div>

      {loading && <div className="text-center p-6"><Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" /></div>}

      {!loading && health && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card className="border-slate-200"><CardContent className="p-4">
              <div className="text-[10px] uppercase tracking-wide text-slate-500">Total Projects</div>
              <div className="text-2xl font-bold text-slate-900" data-testid="ct-total">{health.total_projects}</div>
            </CardContent></Card>
            <Card className="border-emerald-200 bg-emerald-50"><CardContent className="p-4">
              <div className="text-[10px] uppercase tracking-wide text-emerald-700">Healthy</div>
              <div className="text-2xl font-bold text-emerald-800" data-testid="ct-healthy">{health.healthy_count}</div>
            </CardContent></Card>
            <Card className="border-rose-200 bg-rose-50"><CardContent className="p-4">
              <div className="text-[10px] uppercase tracking-wide text-rose-700">At-Risk</div>
              <div className="text-2xl font-bold text-rose-800" data-testid="ct-at-risk">{health.at_risk.length}</div>
            </CardContent></Card>
            <Card className="border-amber-200 bg-amber-50"><CardContent className="p-4">
              <div className="text-[10px] uppercase tracking-wide text-amber-700">Stalled &gt;7d</div>
              <div className="text-2xl font-bold text-amber-800" data-testid="ct-stalled">{health.stalled.length}</div>
            </CardContent></Card>
          </div>

          <Card className="border-slate-200">
            <CardHeader><CardTitle className="text-base">Projects</CardTitle></CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-xs border-collapse" data-testid="ct-projects-table">
                  <thead className="bg-slate-50">
                    <tr>
                      {['Number', 'Name', 'State', 'Health', 'Progress', 'Days idle'].map((h) => (
                        <th key={h} className="text-left px-2 py-1.5 border border-slate-200 font-semibold text-slate-700">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {projects.map((p) => (
                      <tr key={p._id}>
                        <td className="px-2 py-1.5 border border-slate-200 font-mono">{p.project_number}</td>
                        <td className="px-2 py-1.5 border border-slate-200">{p.project_name}</td>
                        <td className="px-2 py-1.5 border border-slate-200"><Badge className="bg-slate-100 text-slate-700 border text-[10px]">{p.status}</Badge></td>
                        <td className="px-2 py-1.5 border border-slate-200">
                          <Badge className={`text-[10px] border ${
                            p.health_status === 'healthy' ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
                              : p.health_status === 'at_risk' ? 'bg-rose-100 text-rose-800 border-rose-200'
                              : 'bg-amber-100 text-amber-800 border-amber-200'}`}>{p.health_status}</Badge>
                        </td>
                        <td className="px-2 py-1.5 border border-slate-200">
                          <div className="w-24 h-2 bg-slate-100 rounded overflow-hidden">
                            <div className="h-full bg-emerald-500" style={{ width: `${p.overall_progress_percent || 0}%` }} />
                          </div>
                        </td>
                        <td className="px-2 py-1.5 border border-slate-200 text-right">
                          {p.days_since_update != null ? `${p.days_since_update}d` : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

// ── B.3 — Test Migration ──────────────────────────────────────────────────

export function TestMigrationPane({ activeProject, setActiveProject }) {
  const projects = useProjects(activeProject, setActiveProject);
  const [runs, setRuns] = useState([]);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [rowLimit, setRowLimit] = useState(5000);

  const load = useCallback(async () => {
    if (!activeProject) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/migration/projects/${activeProject}/test-migration`);
      setRuns(data.runs || []);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  }, [activeProject]);
  useEffect(() => { load(); }, [load]);

  const startRun = async () => {
    if (!activeProject) return;
    setRunning(true);
    try {
      await axios.post(`${API}/api/migration/projects/${activeProject}/test-migration/run`, { row_limit: rowLimit });
      toast.success('Test migration started');
      setTimeout(load, 1500);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
    finally { setRunning(false); }
  };

  const openPreview = async (run) => {
    try {
      const { data } = await axios.get(`${API}/api/migration/projects/${activeProject}/test-migration/${run._id}/preview?limit=20`);
      setPreview({ run, docs: data.docs || [] });
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-4" data-testid="test-migration-pane">
      <div className="flex items-center justify-between gap-4">
        <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
          <Play className="w-5 h-5 text-purple-600" />Test Migration
        </h2>
        <div className="flex gap-2 items-center">
          <ProjectPicker projects={projects} activeProject={activeProject} setActiveProject={setActiveProject} testId="test-mig-project-select" />
          <Input type="number" value={rowLimit} onChange={(e) => setRowLimit(parseInt(e.target.value, 10) || 5000)}
                  className="w-28" data-testid="row-limit-input" placeholder="Rows" />
          <Button size="sm" variant="outline" onClick={load} data-testid="test-mig-refresh-btn"><RefreshCw className="w-4 h-4 mr-2" />Refresh</Button>
          <Button size="sm" onClick={startRun} disabled={running || !activeProject}
                  className="bg-purple-600 hover:bg-purple-700" data-testid="test-mig-start-btn">
            {running ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Queuing…</> : <><Play className="w-4 h-4 mr-2" />Run Test</>}
          </Button>
        </div>
      </div>
      <p className="text-xs text-slate-500">Materialises canonical rows into an isolated <span className="font-mono">migration_shadow_*</span> collection. No production writes.</p>

      {loading && <div className="text-center p-6"><Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" /></div>}

      {!loading && runs.length === 0 && (
        <Card className="border-slate-200"><CardContent className="p-8 text-center text-slate-500">
          No test runs yet. Approve at least one mapping and click <span className="font-semibold">Run Test</span>.
        </CardContent></Card>
      )}

      {runs.map((r) => {
        const canonTotal = Object.values(r.canonical_row_counts_json || {}).reduce((a, b) => a + b, 0);
        return (
          <Card key={r._id} className="border-slate-200" data-testid={`test-run-${r._id}`}>
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-slate-900">Run #{r.run_number}</span>
                  <Badge className={`text-[10px] border ${
                    r.status === 'complete' ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
                      : r.status === 'failed' ? 'bg-rose-100 text-rose-800 border-rose-200'
                      : 'bg-amber-100 text-amber-800 border-amber-200'
                  }`}>{r.status}</Badge>
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  {canonTotal} canonical rows · {Object.keys(r.canonical_row_counts_json || {}).length} object types · row limit {r.row_limit}
                </div>
                {(r.error_messages_json || []).length > 0 && (
                  <div className="text-xs text-rose-700 mt-1">{r.error_messages_json.length} error(s)</div>
                )}
              </div>
              {r.status === 'complete' && (
                <Button size="sm" variant="outline" onClick={() => openPreview(r)} data-testid={`preview-run-${r._id}-btn`}>
                  Preview 20 rows
                </Button>
              )}
            </CardContent>
          </Card>
        );
      })}

      {preview && (
        <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-2 sm:p-4" onClick={() => setPreview(null)}>
          <Card className="w-full max-w-5xl max-h-[92vh] sm:max-h-[85vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="preview-modal">
            <CardHeader className="flex-row items-center justify-between">
              <CardTitle className="text-base">Shadow rows · Run #{preview.run.run_number}</CardTitle>
              <button className="text-slate-400 hover:text-slate-700" onClick={() => setPreview(null)}>✕</button>
            </CardHeader>
            <CardContent>
              <pre className="text-[10px] bg-slate-50 p-3 rounded overflow-auto max-h-[60vh]">
                {JSON.stringify(preview.docs, null, 2)}
              </pre>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

// ── B.4 — Reconciliation ──────────────────────────────────────────────────

export function ReconciliationPane({ activeProject, setActiveProject }) {
  const projects = useProjects(activeProject, setActiveProject);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    if (!activeProject) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/migration/projects/${activeProject}/reconciliation`);
      setReports(data.reports || []);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  }, [activeProject]);
  useEffect(() => { load(); }, [load]);

  const run = async () => {
    if (!activeProject) return;
    setRunning(true);
    try {
      const { data } = await axios.post(`${API}/api/migration/projects/${activeProject}/reconciliation/run`);
      toast.success(`Reconciliation: ${data.report.verdict.toUpperCase()} · ${data.report.match_rate_percent}%`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
    finally { setRunning(false); }
  };

  const badge = (v) => v === 'pass' ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
    : v === 'warning' ? 'bg-amber-100 text-amber-800 border-amber-200'
    : 'bg-rose-100 text-rose-800 border-rose-200';

  return (
    <div className="space-y-4" data-testid="reconciliation-pane">
      <div className="flex items-center justify-between gap-4">
        <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
          <Scale className="w-5 h-5 text-indigo-600" />Reconciliation
        </h2>
        <div className="flex gap-2 items-center">
          <ProjectPicker projects={projects} activeProject={activeProject} setActiveProject={setActiveProject} testId="recon-project-select" />
          <Button size="sm" variant="outline" onClick={load} data-testid="recon-refresh-btn"><RefreshCw className="w-4 h-4 mr-2" />Refresh</Button>
          <Button size="sm" onClick={run} disabled={running || !activeProject}
                  className="bg-indigo-600 hover:bg-indigo-700" data-testid="recon-run-btn">
            {running ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Running…</> : <><Scale className="w-4 h-4 mr-2" />Reconcile</>}
          </Button>
        </div>
      </div>

      {loading && <div className="text-center p-6"><Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" /></div>}

      {!loading && reports.length === 0 && (
        <Card className="border-slate-200"><CardContent className="p-8 text-center text-slate-500">No reconciliation reports yet.</CardContent></Card>
      )}

      {reports.map((r) => (
        <Card key={r._id} className="border-slate-200" data-testid={`recon-report-${r._id}`}>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base">Report {new Date(r.generated_at).toLocaleString()}</CardTitle>
                <div className="text-xs text-slate-500 mt-1">{r.total_source_rows.toLocaleString()} source vs {r.total_shadow_rows.toLocaleString()} shadow rows</div>
              </div>
              <div className="flex gap-3 items-center">
                <div className="text-center">
                  <div className="text-xl font-bold text-slate-900">{r.match_rate_percent}%</div>
                  <div className="text-[10px] text-slate-500 uppercase">Match</div>
                </div>
                <Badge className={`text-xs border ${badge(r.verdict)}`}>{r.verdict.toUpperCase()}</Badge>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {(r.discrepancies_json || []).length > 0 && (
              <div className="space-y-2">
                <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Discrepancies</div>
                {r.discrepancies_json.map((d, i) => (
                  <div key={i} className="p-2 border rounded-md bg-amber-50 border-amber-200 text-xs">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-3 h-3 text-amber-600" />
                      <span className="font-semibold">{d.entity_name}</span>
                      <Badge className="bg-white text-amber-700 border border-amber-200 text-[10px]">{d.kind}</Badge>
                    </div>
                    <div className="text-slate-600 mt-1">{d.description}</div>
                  </div>
                ))}
              </div>
            )}
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                <thead className="bg-slate-50">
                  <tr>{['Entity', 'Target', 'Source Rows', 'Shadow Rows', 'Match %'].map((h) => (
                    <th key={h} className="text-left px-2 py-1.5 border border-slate-200 font-semibold text-slate-700">{h}</th>
                  ))}</tr>
                </thead>
                <tbody>
                  {(r.per_entity_json || []).map((e, i) => (
                    <tr key={i}>
                      <td className="px-2 py-1.5 border border-slate-200">{e.entity_name}</td>
                      <td className="px-2 py-1.5 border border-slate-200 font-mono">{e.canonical_object_key}</td>
                      <td className="px-2 py-1.5 border border-slate-200 text-right">{e.source_rows}</td>
                      <td className="px-2 py-1.5 border border-slate-200 text-right">{e.shadow_rows}</td>
                      <td className="px-2 py-1.5 border border-slate-200 text-right">{e.match_rate_percent}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ── B.5 — UAT & Release ────────────────────────────────────────────────────

export function ReleasePane({ activeProject, setActiveProject }) {
  const projects = useProjects(activeProject, setActiveProject);
  const [approvals, setApprovals] = useState([]);
  const [manifests, setManifests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!activeProject) return;
    setLoading(true);
    try {
      const [u, m] = await Promise.all([
        axios.get(`${API}/api/migration/projects/${activeProject}/uat`),
        axios.get(`${API}/api/migration/projects/${activeProject}/release`),
      ]);
      setApprovals(u.data.approvals || []);
      setManifests(m.data.manifests || []);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  }, [activeProject]);
  useEffect(() => { load(); }, [load]);

  const initiate = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/api/migration/projects/${activeProject}/uat/initiate`);
      toast.success('UAT initiated');
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const sign = async (approval_id, decision) => {
    try {
      await axios.post(`${API}/api/migration/projects/${activeProject}/uat/${approval_id}/sign`, { decision });
      toast.success(`UAT ${decision}d`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const buildManifest = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/migration/projects/${activeProject}/release/build-manifest`);
      toast.success(`Manifest built · ${data.manifest.forward_row_count} rows planned`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const executeRelease = async (mid) => {
    if (!window.confirm('Execute production writes? This is reversible via Rollback, but writes real rows into business tables.')) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/migration/projects/${activeProject}/release/${mid}/execute`);
      toast.success(`Release executed · ${data.success_count} rows written · ${data.error_count} errors`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="release-pane">
      <div className="flex items-center justify-between gap-4">
        <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
          <ClipboardCheck className="w-5 h-5 text-emerald-600" />UAT &amp; Release
        </h2>
        <div className="flex gap-2 items-center">
          <ProjectPicker projects={projects} activeProject={activeProject} setActiveProject={setActiveProject} testId="release-project-select" />
          <Button size="sm" variant="outline" onClick={load} data-testid="release-refresh-btn"><RefreshCw className="w-4 h-4 mr-2" />Refresh</Button>
        </div>
      </div>

      {loading && <div className="text-center p-6"><Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" /></div>}

      <Card className="border-slate-200">
        <CardHeader className="flex-row items-center justify-between">
          <CardTitle className="text-base">UAT Approvals ({approvals.length})</CardTitle>
          {approvals.length === 0 && (
            <Button size="sm" onClick={initiate} disabled={busy || !activeProject}
                    className="bg-emerald-600 hover:bg-emerald-700" data-testid="uat-initiate-btn">
              Initiate UAT
            </Button>
          )}
        </CardHeader>
        <CardContent className="space-y-2">
          {approvals.length === 0 && <div className="text-xs text-slate-500 text-center p-3">No UAT rounds started. Approvals require a passing reconciliation report.</div>}
          {approvals.map((a) => (
            <div key={a._id} className="p-2 border rounded-md bg-slate-50 border-slate-200 flex items-center justify-between" data-testid={`uat-${a._id}`}>
              <div>
                <div className="text-sm font-medium text-slate-800 font-mono">{a.approver_role_required}</div>
                <div className="text-[10px] text-slate-500">Status: {a.status}</div>
              </div>
              {a.status === 'pending' ? (
                <div className="flex gap-1">
                  <button onClick={() => sign(a._id, 'approve')}
                          data-testid={`uat-approve-${a._id}`}
                          className="text-emerald-700 hover:bg-emerald-50 p-1 rounded"><CheckCircle2 className="w-4 h-4" /></button>
                  <button onClick={() => sign(a._id, 'reject')}
                          data-testid={`uat-reject-${a._id}`}
                          className="text-rose-700 hover:bg-rose-50 p-1 rounded"><XCircle className="w-4 h-4" /></button>
                </div>
              ) : (
                <Badge className={`text-[10px] border ${
                  a.status === 'approved' ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
                    : 'bg-rose-100 text-rose-800 border-rose-200'}`}>{a.status}</Badge>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardHeader className="flex-row items-center justify-between">
          <CardTitle className="text-base">Release Manifests</CardTitle>
          <Button size="sm" onClick={buildManifest} disabled={busy || !activeProject}
                  variant="outline" data-testid="release-build-btn">
            Build Manifest
          </Button>
        </CardHeader>
        <CardContent className="space-y-2">
          {manifests.length === 0 && <div className="text-xs text-slate-500 text-center p-3">Build a manifest after all UAT approvals are signed.</div>}
          {manifests.map((m) => (
            <div key={m._id} className="p-2 border rounded-md bg-slate-50 border-slate-200" data-testid={`manifest-${m._id}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-semibold text-slate-800">Manifest #{m.manifest_number}</div>
                  <div className="text-[10px] text-slate-500 font-mono">{m.forward_plan_json}</div>
                  {m.status === 'executed' && (
                    <div className="text-[10px] text-emerald-700">✓ {m.executed_success_count} rows written · {m.executed_error_count} errors</div>
                  )}
                  {m.status === 'reversed' && (
                    <div className="text-[10px] text-purple-700">↶ {m.reversed_row_count} rows reversed</div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={`text-[10px] border ${
                    m.status === 'executed' ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
                      : m.status === 'ready' ? 'bg-amber-100 text-amber-800 border-amber-200'
                      : m.status === 'reversed' ? 'bg-purple-100 text-purple-800 border-purple-200'
                      : 'bg-slate-100 text-slate-700 border-slate-200'}`}>{m.status}</Badge>
                  {m.status === 'ready' && (
                    <Button size="sm" onClick={() => executeRelease(m._id)} disabled={busy}
                            className="bg-emerald-600 hover:bg-emerald-700" data-testid={`release-execute-${m._id}-btn`}>
                      Execute
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

// ── B.6 — Rollback & Hypercare ────────────────────────────────────────────

export function HypercarePane({ activeProject, setActiveProject }) {
  const projects = useProjects(activeProject, setActiveProject);
  const [checks, setChecks] = useState([]);
  const [manifests, setManifests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState('healthy');
  const [errorCount, setErrorCount] = useState(0);
  const [rowDrift, setRowDrift] = useState(0);
  const [notes, setNotes] = useState('');

  const load = useCallback(async () => {
    if (!activeProject) return;
    setLoading(true);
    try {
      const [c, m] = await Promise.all([
        axios.get(`${API}/api/migration/projects/${activeProject}/hypercare`),
        axios.get(`${API}/api/migration/projects/${activeProject}/release`),
      ]);
      setChecks(c.data.checks || []);
      setManifests(m.data.manifests || []);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  }, [activeProject]);
  useEffect(() => { load(); }, [load]);

  const rollback = async (mid) => {
    if (!window.confirm('Rollback this release? All rows written by this manifest will be REMOVED from business tables.')) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/migration/projects/${activeProject}/release/${mid}/rollback`);
      toast.success(`Rolled back · ${data.reversed_count} rows removed`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const submitCheck = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/api/migration/projects/${activeProject}/hypercare/check`, {
        status, error_count: errorCount, row_count_drift: rowDrift, notes,
      });
      toast.success('Hypercare check logged');
      setNotes(''); setErrorCount(0); setRowDrift(0);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const close = async () => {
    if (!window.confirm('Close this migration project? This is a terminal state.')) return;
    setBusy(true);
    try {
      await axios.post(`${API}/api/migration/projects/${activeProject}/close`);
      toast.success('Project closed');
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const executed = manifests.filter((m) => m.status === 'executed');

  return (
    <div className="space-y-4" data-testid="hypercare-pane">
      <div className="flex items-center justify-between gap-4">
        <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
          <RotateCcw className="w-5 h-5 text-purple-600" />Rollback &amp; Hypercare
        </h2>
        <div className="flex gap-2 items-center">
          <ProjectPicker projects={projects} activeProject={activeProject} setActiveProject={setActiveProject} testId="hyper-project-select" />
          <Button size="sm" variant="outline" onClick={load} data-testid="hyper-refresh-btn"><RefreshCw className="w-4 h-4 mr-2" />Refresh</Button>
        </div>
      </div>

      {loading && <div className="text-center p-6"><Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" /></div>}

      {executed.length > 0 && (
        <Card className="border-slate-200">
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><ShieldAlert className="w-4 h-4 text-purple-600" />Executed Releases (rollback-eligible)</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {executed.map((m) => (
              <div key={m._id} className="p-2 border rounded-md bg-purple-50 border-purple-200 flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-slate-800">Manifest #{m.manifest_number}</div>
                  <div className="text-[10px] text-slate-500">
                    Executed {new Date(m.executed_at_optional).toLocaleString()} · {m.executed_success_count} rows
                  </div>
                </div>
                <Button size="sm" onClick={() => rollback(m._id)} disabled={busy}
                        className="text-purple-700 border-purple-300 hover:bg-purple-100" variant="outline"
                        data-testid={`rollback-${m._id}-btn`}>
                  <RotateCcw className="w-4 h-4 mr-2" />Rollback
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Card className="border-slate-200">
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><TrendingUp className="w-4 h-4 text-emerald-600" />Log a hypercare check</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2 items-end">
            <div>
              <Label className="text-xs">Status</Label>
              <select value={status} onChange={(e) => setStatus(e.target.value)}
                       className="border border-slate-200 rounded-md h-9 px-3 text-sm bg-white w-full"
                       data-testid="hyper-status-select">
                <option value="healthy">Healthy</option>
                <option value="warning">Warning</option>
                <option value="critical">Critical</option>
              </select>
            </div>
            <div>
              <Label className="text-xs">Errors observed</Label>
              <Input type="number" value={errorCount} onChange={(e) => setErrorCount(parseInt(e.target.value, 10) || 0)} data-testid="hyper-errors-input" />
            </div>
            <div>
              <Label className="text-xs">Row-count drift</Label>
              <Input type="number" value={rowDrift} onChange={(e) => setRowDrift(parseInt(e.target.value, 10) || 0)} data-testid="hyper-drift-input" />
            </div>
            <div>
              <Label className="text-xs">Notes</Label>
              <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional" data-testid="hyper-notes-input" />
            </div>
          </div>
          <div className="mt-3 flex gap-2">
            <Button size="sm" onClick={submitCheck} disabled={busy || !activeProject}
                    className="bg-emerald-600 hover:bg-emerald-700" data-testid="hyper-log-btn">
              Log check
            </Button>
            <Button size="sm" onClick={close} disabled={busy || !activeProject} variant="outline" data-testid="project-close-btn">
              Close project
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardHeader><CardTitle className="text-base">Check history ({checks.length})</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {checks.length === 0 && <div className="text-xs text-slate-500 text-center p-3">No checks logged.</div>}
          {checks.map((c) => (
            <div key={c._id} className="p-2 border rounded-md bg-slate-50 border-slate-200 flex items-center justify-between" data-testid={`hyper-check-${c._id}`}>
              <div>
                <div className="text-sm font-medium text-slate-800">Check #{c.check_number} — {new Date(c.checked_at).toLocaleString()}</div>
                <div className="text-[10px] text-slate-500">Errors: {c.error_count} · Drift: {c.row_count_drift}</div>
                {c.notes && <div className="text-xs text-slate-600 mt-1">{c.notes}</div>}
              </div>
              <Badge className={`text-[10px] border ${
                c.status === 'healthy' ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
                  : c.status === 'warning' ? 'bg-amber-100 text-amber-800 border-amber-200'
                  : 'bg-rose-100 text-rose-800 border-rose-200'}`}>{c.status}</Badge>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
